import { InventoryImportDialog } from '@/components/inventory/inventory-import-dialog';
import { InventoryTable } from '@/components/inventory/inventory-table';
import { Layout } from '@/components/layouts/layout';
import { useInventory, useInventorySearch } from '@/hooks/use-inventory';
import type { InventoryFilters } from '@/lib/inventory-api';
import { createFileRoute } from '@tanstack/react-router';
import { type ColumnFiltersState, type RowData } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';

declare module '@tanstack/react-table' {
    interface ColumnMeta<TData extends RowData, TValue> {
        className?: string;
        headerClassName?: string;
        __types?: [TData, TValue];
    }
}

export const Route = createFileRoute('/inventory/')({
    validateSearch: (search: Record<string, unknown>) => ({
        q: typeof search.q === 'string' ? search.q : '',
    }),
    component: InventoryPage,
});

function buildServerFilters(
    columnFilters: ColumnFiltersState,
): InventoryFilters {
    const lookup = new Map(
        columnFilters.map((filter) => [filter.id, filter.value]),
    );
    const toValue = (value: unknown) =>
        typeof value === 'string' ? value.trim() : '';

    const brand = toValue(lookup.get('Brand'));
    const description = toValue(lookup.get('Description'));
    const status = toValue(lookup.get('Item Status'));
    const dateListed = toValue(lookup.get('Date Listed'));
    const dateSold = toValue(lookup.get('Date Sold'));
    const aging = toValue(lookup.get('aging'));
    const platform = toValue(lookup.get('platform'));

    return {
        ...(brand ? { brand } : {}),
        ...(description ? { description } : {}),
        ...(status && status !== 'all' ? { status } : {}),
        ...(dateListed ? { dateListed } : {}),
        ...(dateSold ? { dateSold } : {}),
        ...(aging && aging !== 'all' ? { aging } : {}),
        ...(platform ? { platform } : {}),
    };
}

function InventoryPage() {
    const { q: initialQ } = Route.useSearch();
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 25,
    });
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
        { id: 'Item Status', value: '' },
    ]);
    const [searchQuery, setSearchQuery] = useState(initialQ);
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialQ);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSearchChange = (q: string) => {
        setSearchQuery(q);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(
            () => setDebouncedSearchQuery(q),
            200,
        );
    };

    const isSearching = debouncedSearchQuery.trim().length > 0;

    const filters = useMemo(
        () => buildServerFilters(columnFilters),
        [columnFilters],
    );
    const filtersKey = JSON.stringify(filters) + debouncedSearchQuery;

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPagination((current) =>
            current.pageIndex === 0 ? current : { ...current, pageIndex: 0 },
        );
    }, [filtersKey]);

    const statusFilter = filters.status;

    const browseQuery = useInventory(
        pagination.pageIndex + 1,
        pagination.pageSize,
        filters,
    );
    const typesenseQuery = useInventorySearch(
        debouncedSearchQuery,
        pagination.pageIndex + 1,
        pagination.pageSize,
        { status: statusFilter },
    );

    const { data, isLoading, isError, error } = isSearching
        ? typesenseQuery
        : browseQuery;

    const rows = data?.list ?? [];
    const totalRows = data?.pageInfo.totalRows ?? rows.length;
    const pageCount = Math.max(1, Math.ceil(totalRows / pagination.pageSize));

    return (
        <Layout>
            <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold">Inventory</h1>
                        <p className="text-sm text-muted-foreground">
                            Track what is listed, sold, and in progress.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <InventoryImportDialog />
                        {/* <Button asChild>
                            <Link to="/add">Add Item</Link>
                        </Button> */}
                    </div>
                </div>

                <div className="w-full overflow-x-auto">
                    <InventoryTable
                        rows={rows}
                        totalRows={totalRows}
                        pageCount={pageCount}
                        pagination={pagination}
                        onPaginationChange={setPagination}
                        columnFilters={columnFilters}
                        onColumnFiltersChange={setColumnFilters}
                        searchQuery={searchQuery}
                        onSearchQueryChange={handleSearchChange}
                        isLoading={isLoading}
                        isError={isError}
                        error={error}
                    />
                </div>
            </div>
        </Layout>
    );
}
